import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthenticationService } from './authentication/authentication.service';

export interface OnboardingState {
  isCompleted: boolean;
  currentStep: number;
  skipped: boolean;
  completedSteps: number[];
  hasExistingCompany: boolean;
  existingCompanyId?: number;
  userData?: {
    name?: string;
    email?: string;
    phone?: string;
    lastName?: string;
  };
  companyData?: {
    id?: number;
    name?: string;
    principalColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    base1Color?: string;
    base2Color?: string;
    logoId?: number;
  };
  completedAt?: string;
}

/**
 * Servicio para gestionar el estado del onboarding wizard
 */
@Injectable({
  providedIn: 'root'
})
export class OnboardingService {
  private readonly STORAGE_KEY_PREFIX = 'screenleads_onboarding_state';
  private readonly LAST_STEP_INDEX = 4;
  private authService = inject(AuthenticationService);

  private ownerKey = this.getStorageKey();
  private stateSubject = new BehaviorSubject<OnboardingState>(this.loadState());
  public state$ = this.stateSubject.asObservable();

  constructor() {
    this.detectExistingCompany();
  }

  /**
   * Detecta si el usuario ya tiene una empresa asignada
   */
  private detectExistingCompany(): void {
    const user = this.authService.getUser();
    const companyId = user?.company?.id ?? user?.companyId;
    if (companyId) {
      const currentState = this.stateSubject.value;
      this.updateState({
        ...currentState,
        hasExistingCompany: true,
        existingCompanyId: companyId
      });
    }
  }

  /**
   * Verifica si el usuario tiene una empresa existente
   */
  hasExistingCompany(): boolean {
    this.ensureOwner();
    return this.stateSubject.value.hasExistingCompany || false;
  }

  /**
   * Obtiene el ID de la empresa existente
   */
  getExistingCompanyId(): number | undefined {
    this.ensureOwner();
    return this.stateSubject.value.existingCompanyId;
  }

  /**
   * Obtiene el estado actual del onboarding
   */
  getState(): OnboardingState {
    this.ensureOwner();
    return this.stateSubject.value;
  }

  /**
   * Verifica si el usuario ya completó el onboarding
   */
  isOnboardingCompleted(): boolean {
    this.ensureOwner();
    return this.stateSubject.value.isCompleted || this.stateSubject.value.skipped;
  }

  /**
   * Verifica si un paso específico está completado
   */
  isStepCompleted(step: number): boolean {
    this.ensureOwner();
    return this.stateSubject.value.completedSteps.includes(step);
  }

  /**
   * Marca el paso actual como completado y avanza al siguiente
   */
  completeStep(step: number): void {
    this.ensureOwner();
    const currentState = this.stateSubject.value;
    const completedSteps = [...currentState.completedSteps];

    if (!completedSteps.includes(step)) {
      completedSteps.push(step);
    }

    const newState: OnboardingState = {
      ...currentState,
      completedSteps,
      currentStep: Math.min(this.LAST_STEP_INDEX, step + 1)
    };

    this.updateState(newState);
  }

  /**
   * Establece el paso actual
   */
  setCurrentStep(step: number): void {
    this.ensureOwner();
    const newState: OnboardingState = {
      ...this.stateSubject.value,
      currentStep: Math.min(this.LAST_STEP_INDEX, Math.max(0, step))
    };
    this.updateState(newState);
  }

  /**
   * Guarda los datos del usuario
   */
  saveUserData(data: Partial<OnboardingState['userData']>): void {
    this.ensureOwner();
    const newState: OnboardingState = {
      ...this.stateSubject.value,
      userData: {
        ...this.stateSubject.value.userData,
        ...data
      }
    };
    this.updateState(newState);
  }

  /**
   * Guarda los datos de la empresa
   */
  saveCompanyData(data: Partial<OnboardingState['companyData']>): void {
    this.ensureOwner();
    const newState: OnboardingState = {
      ...this.stateSubject.value,
      companyData: {
        ...this.stateSubject.value.companyData,
        ...data
      }
    };
    this.updateState(newState);
  }

  /**
   * Marca el onboarding como completado
   */
  completeOnboarding(): void {
    this.ensureOwner();
    const newState: OnboardingState = {
      ...this.stateSubject.value,
      isCompleted: true,
      skipped: false,
      currentStep: this.LAST_STEP_INDEX,
      completedSteps: [0,1,2,3,4],
      completedAt: new Date().toISOString()
    };
    this.updateState(newState);
  }

  /**
   * Permite al usuario saltarse el onboarding
   */
  skipOnboarding(): void {
    this.ensureOwner();
    const newState: OnboardingState = {
      ...this.stateSubject.value,
      skipped: true,
      completedAt: new Date().toISOString()
    };
    this.updateState(newState);
  }

  /**
   * Reinicia el estado del onboarding
   */
  resetOnboarding(): void {
    const initialState = this.getInitialState();
    this.ensureOwner();
    this.updateState(initialState);
    this.detectExistingCompany();
  }

  /**
   * Actualiza el estado y lo persiste
   */
  private updateState(newState: OnboardingState): void {
    this.stateSubject.next(newState);
    this.saveState(newState);
  }

  /**
   * Carga el estado desde localStorage
   */
  private loadState(): OnboardingState {
    try {
      const saved = localStorage.getItem(this.getStorageKey());
      if (saved) {
        const parsed=JSON.parse(saved),initial=this.getInitialState();
        if(!parsed||typeof parsed!=='object')return initial;
        return {...initial,...parsed,isCompleted:parsed.isCompleted===true,skipped:parsed.skipped===true,currentStep:Number.isInteger(parsed.currentStep)?Math.min(4,Math.max(0,parsed.currentStep)):0,completedSteps:Array.isArray(parsed.completedSteps)?parsed.completedSteps.filter((s:unknown)=>Number.isInteger(s)&&Number(s)>=0&&Number(s)<=4):[]};
      }
    } catch (error) {
      console.error('Error loading onboarding state:', error);
    }
    return this.getInitialState();
  }

  /**
   * Guarda el estado en localStorage
   */
  private saveState(state: OnboardingState): void {
    try {
      localStorage.setItem(this.getStorageKey(), JSON.stringify(state));
    } catch (error) {
      console.error('Error saving onboarding state:', error);
    }
  }

  private ensureOwner(): void {
    const key=this.getStorageKey();
    if(key===this.ownerKey)return;
    this.ownerKey=key;
    this.stateSubject.next(this.loadState());
    this.detectExistingCompany();
  }

  private getStorageKey(): string {
    const user = this.authService.getUser();
    const userId = user?.id ?? user?.userId ?? 'anonymous';
    return `${this.STORAGE_KEY_PREFIX}_${userId}`;
  }

  /**
   * Obtiene el estado inicial
   */
  private getInitialState(): OnboardingState {
    return {
      isCompleted: false,
      currentStep: 0,
      skipped: false,
      completedSteps: [],
      hasExistingCompany: false,
      userData: {},
      companyData: {}
    };
  }
}
